// client/src/pages/CentralConfig/index.jsx
/**
 * Page /config — « Connexion au central » : relie CE serveur à l'administration
 * centrale (ticket-admin.amia.fr). Le manager saisit l'URL du central, l'identifiant
 * de site et le TOKEN (provisionné au central), puis connecte. Réglage local persisté
 * en base (data-plug) ; le token n'est jamais réaffiché.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import controlPlaneService from '../../services/controlPlaneService';

function CentralConfig() {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ url: '', siteId: '', token: '' });
  const [busy, setBusy] = useState(false);

  const refresh = () => controlPlaneService.get()
    .then((d) => setSt(d))
    .catch((e) => toast.error(e?.response?.data?.error || e?.message || 'Erreur'));

  useEffect(() => {
    controlPlaneService.get()
      .then((d) => { setSt(d); setForm({ url: d.url || '', siteId: d.siteId || '', token: '' }); })
      .catch((e) => toast.error(e?.response?.data?.error || 'Erreur de chargement'));
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const d = await controlPlaneService.save({
        enabled: true,
        url: form.url.trim(),
        siteId: form.siteId.trim(),
        ...(form.token ? { token: form.token } : {}),
      });
      setSt(d); setForm((f) => ({ ...f, token: '' }));
      toast.success(d.connected ? 'Connecté au central' : 'Enregistré — connexion en cours…');
    } catch (e) { toast.error(e?.response?.data?.error || 'Échec'); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { setSt(await controlPlaneService.disconnect()); toast.info('Déconnecté'); }
    catch (e) { toast.error(e?.response?.data?.error || 'Échec'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 md:p-10">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Connexion au central</h1>
          <button onClick={() => navigate('/manage')} className="text-sm text-gray-600 hover:text-gray-900 underline">← Menu</button>
        </div>

        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          {!st ? <p className="text-gray-400">Chargement…</p> : (
            <>
              <div className="mb-4 text-sm">
                État :{' '}
                {st.connected
                  ? <span className="inline-block bg-green-100 text-green-700 rounded px-2 py-0.5">connecté</span>
                  : st.enabled
                    ? <span className="inline-block bg-yellow-100 text-yellow-700 rounded px-2 py-0.5">connexion…</span>
                    : <span className="inline-block bg-gray-100 text-gray-600 rounded px-2 py-0.5">inactif</span>}
                {st.lastError && !st.connected && <span className="text-red-600 ml-2">{st.lastError}</span>}
              </div>

              <label className="block text-xs text-gray-500 mb-1">URL du central</label>
              <input className="w-full border rounded px-3 py-2 mb-3 text-sm" placeholder="https://ticket-admin.amia.fr"
                value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />

              <label className="block text-xs text-gray-500 mb-1">Identifiant de site</label>
              <input className="w-full border rounded px-3 py-2 mb-3 text-sm" placeholder="cabinet-alger"
                value={form.siteId} onChange={(e) => setForm({ ...form, siteId: e.target.value })} />

              <label className="block text-xs text-gray-500 mb-1">Token de connexion {st.hasToken ? '(enregistré)' : ''}</label>
              <input type="password" className="w-full border rounded px-3 py-2 mb-4 text-sm"
                placeholder={st.hasToken ? '•••• (laisser vide pour garder)' : 'coller le token fourni par le central'}
                value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} />

              <div className="flex gap-3">
                <button onClick={connect} disabled={busy || !form.url || !form.siteId || (!st.hasToken && !form.token)}
                  className="bg-blue-600 text-white text-sm px-5 py-2 rounded hover:bg-blue-700 disabled:opacity-50">Connecter</button>
                <button onClick={disconnect} disabled={busy || !st.enabled}
                  className="border border-gray-300 text-gray-700 text-sm px-5 py-2 rounded hover:bg-gray-50 disabled:opacity-50">Déconnecter</button>
              </div>
              <p className="text-xs text-gray-400 mt-4">Le token est fourni par la console centrale (provisioning) ; il n'est jamais réaffiché.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CentralConfig;
